import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import type { Task } from 'ember-concurrency';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import { or } from 'ember-truth-helpers';
import CloseButton from 'garaje/components/close-button';
import ButtonGroup from 'garaje/components/ui/button-group';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import ecTask from 'garaje/helpers/ec-task';
import isTask from 'garaje/helpers/is-task';

interface ConfirmationModalSignature {
  Args: {
    cancelText?: string;
    closeButtonClass?: string;
    containerClass?: string;
    ctaText: string;
    disableCTA?: boolean;
    handleDelete?: (event?: MouseEvent) => void;
    handleTaskAction: Task<void, [MouseEvent]> | ((event?: MouseEvent) => void);
    hideCtaButtons?: boolean;
    onClose: (event: MouseEvent) => void;
    overlayClass?: string;
    showTopRightCloseButton?: boolean;
    showTopRightCloseX?: boolean;
    title: string;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}
export default class ConfirmationModal extends Component<ConfirmationModalSignature> {
  get task(): Task<unknown, [MouseEvent]> {
    const handleTaskAction = this.args.handleTaskAction;

    if (isTask(handleTaskAction)) {
      return handleTaskAction;
    }
    const taskifier = new ecTask();
    return taskifier.compute([<(event: unknown) => void>handleTaskAction], {});
  }

  <template>
    <ModalDialog
      @clickOutsideToClose={{false}}
      @translucentOverlay={{true}}
      @onClose={{@onClose}}
      @overlayClass={{@overlayClass}}
      @containerClass={{@containerClass}}
    >
      <FocusTrapper>
        {{#if @showTopRightCloseX}}
          <CloseButton class="right-6 top-6 !bg-white !p-0 w-10 h-10 fixed" style="min-width:0" @onClick={{@onClose}} />
        {{/if}}
        <div data-test-confirmation-modal class="p-8" ...attributes>
          <h1 class="text-2xl">{{@title}}</h1>
          {{#if @showTopRightCloseButton}}
            <button
              type="button"
              class="btn btn--small btn--outline btn--cancel absolute right-6 top-6"
              {{on "click" @onClose}}
            >
              <span aria-hidden="true">&times;</span>
              <span class="sr-only">Close</span>
            </button>
          {{/if}}
          {{yield}}
          <footer class="pt-8 flex {{if @handleDelete 'justify-between' 'justify-end'}}">
            {{#if @handleDelete}}
              <div>
                <button
                  data-test-confirmation-modal-delete
                  type="button"
                  class="btn btn--hoverFill float-right"
                  {{on "click" @handleDelete}}
                >
                  <span class="icon-trash"></span>
                  Delete
                </button>
              </div>
            {{/if}}
            {{#unless @hideCtaButtons}}
              <ButtonGroup @reverse={{true}}>
                <button
                  data-test-confirmation-modal-action
                  type="button"
                  class="btn btn--small btn--brand"
                  disabled={{or @disableCTA this.task.isRunning}}
                  {{on "click" this.task.perform}}
                >
                  {{@ctaText}}
                </button>
                <button
                  data-test-confirmation-modal-cancel
                  type="button"
                  class="btn btn--small {{if @closeButtonClass @closeButtonClass 'btn--nonHoverFill'}}"
                  disabled={{this.task.isRunning}}
                  {{on "click" @onClose}}
                >
                  {{or @cancelText "Cancel"}}
                </button>
              </ButtonGroup>
            {{/unless}}
          </footer>
        </div>
      </FocusTrapper>
    </ModalDialog>
  </template>
}
